"use client";
import pulseConfig from "@/pulsex.config";
import { useMenus } from "@pulsex/hooks";
import { Logo } from "@/icons/Logo";
import { cn } from "@pulsex/utils";
import { locale } from "@/locale";
import Link from "next/link";
import { Menu } from "@pulsex/models";
import {
  ALAKHBAR_FACEBOOK,
  ALAKHBAR_INSTAGRAM,
  ALAKHBAR_X,
  ALAKHBAR_YOUTUBE,
} from "@/constants";
import FacebookIcon from "@/icons/FacebookIcon";
import InstagramIcon from "@/icons/InstagramIcon";
import XIcon from "@/icons/XIcon";
import YouTubeIcon from "@/icons/YoutubeIcon";
import TikTokIcon from "@/icons/TikTok";
import WhatsAppIcon from "@/icons/WhatsAppIcon";
import TelegramIcon from "@/icons/TelegramIcon";
import { siteHref } from "@/utils/links";

export const Footer = ({ isMobile }: any) => {
  const { menus, status } = useMenus(pulseConfig);

  const currentYear = new Date().getFullYear();

  return (
    <footer
      className={cn(
        "container lg:w-[calc(100%-180px)] lg:mx-auto flex max-md:flex-col mt-8 lg:mt-12"
      )}
    >
      <div
        className={cn(
          "w-full py-7 px-6 max-md:px-2 bg-lightgreyBackground rounded-t-2xl"
        )}
      >
        <a href="/" aria-label="home" className="w-fit mx-auto block">
          <Logo className="fill-black" size={52} />
        </a>

        {menus ? (
          <div className="w-fit mx-auto flex flex-row-reverse mt-8 items-center">
            <Socials
              menus={menus}
              className="mr-5 ml-0 lg:mr-7"
              iconclassName="max-sm:w-5 max-sm:h-5"
              iconSize={25}
            />
          </div>
        ) : (
          ""
        )}
        <ul className="mt-6 flex max-md:justify-between text-2xl max-md:text-xl max-[360px]:text-base text-white py-1 px-8 max-md:px-3 bg-masterRed rounded-full font-amine-light mx-auto w-fit">
          {menus
            ?.getMenuByKey("h-footer")
            ?.map((menuItem: any, index: number) => (
              <li
                key={index}
                className={cn(
                  "ml-1.5 md:ml-8 lg:ml-10 lg:hover:text-white/85 transition-opacity max-md:text-base",
                  index === menus?.getMenuByKey("h-footer").length - 1 &&
                    "!ml-0"
                )}
              >
                <Link href={siteHref(menuItem?.url)}>{menuItem?.title} </Link>
              </li>
            ))}
        </ul>

        <p className="mx-auto w-fit max-lg:text-center text-base md:text-lg leading-2 mt-4 max-md:px-3">
          {locale.footerSubMessage} {currentYear}
          <span className="mt-1 h-fit text-sm sm:text-lg">©</span>
        </p>
        <p className="mx-auto w-fit max-lg:text-center max-md:mt-1 text-base leading-2 md:text-lg max-lg:max-w-lg  max-md:px-4">
          {locale.footerMainMessage}
        </p>
      </div>
    </footer>
  );
};

const socialUrls = {
  facebook: ALAKHBAR_FACEBOOK,
  instagram: ALAKHBAR_INSTAGRAM,
  twitter: ALAKHBAR_X,
  youtube: ALAKHBAR_YOUTUBE,
} as Record<string, string>;

const Socials = ({
  menus,
  className,
  iconSize = 20,
  iconclassName,
}: {
  menus: Menu;
  className?: string;
  iconSize?: number;
  iconclassName?: string;
}) => {
  const socials = menus?.menus?.["h-footer-socials"] ?? [];

  return socials.map((item: any, index: number) => {
    const title = item.title?.toLowerCase();
    const url = item.url;

    const commonProps = {
      key: index,
      url: url || socialUrls[title],
      target: "_blank",
      rel: "noopener noreferrer",
      "aria-label": `${title} social`,
      className: cn(
        "ml-2.5 h-fit w-fit grid place-items-center",
        index === socials.length - 1 && "!mr-0",
        className
      ),
      iconclassName: iconclassName,
    };

    switch (title) {
      // case "facebook":
      //   return (
      //     <a
      //       key={commonProps.key}
      //       rel={commonProps.rel}
      //       target={commonProps.target}
      //       href={commonProps.url}
      //       className={commonProps.className}
      //       aria-label={commonProps["aria-label"]}
      //     >
      //       <FacebookIcon
      //         size={iconSize}
      //         className={cn("fill-masterRed", commonProps.iconclassName)}
      //       />
      //     </a>
      //   );
      case "instagram":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <InstagramIcon
              size={iconSize - 3}
              className={cn("stroke-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      case "twitter":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <XIcon
              size={iconSize - 1}
              className={cn("fill-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      case "youtube":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <YouTubeIcon
              size={iconSize}
              className={cn("fill-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      case "tiktok":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <TikTokIcon
              size={iconSize}
              className={cn("fill-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      case "whatsapp":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <WhatsAppIcon
              size={iconSize - 2}
              className={cn("fill-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      case "telegram":
        return (
          <a
            key={commonProps.key}
            rel={commonProps.rel}
            target={commonProps.target}
            href={commonProps.url}
            className={commonProps.className}
            aria-label={commonProps["aria-label"]}
          >
            <TelegramIcon
              size={iconSize + 1}
              className={cn("fill-masterRed", commonProps.iconclassName)}
            />
          </a>
        );
      default:
        return null;
    }
  });
};
